import React, { useEffect, useState } from 'react';
import { BellIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { userAPI } from '../../services/api';

const Header = ({ toggleSidebar }) => {
  const [user, setUser] = useState({ name: '', profileImage: '' });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Helper function to get correct image URL
  const getProfileImageUrl = (imagePath) => {
    if (!imagePath) return "https://cdn-icons-png.flaticon.com/512/149/149071.png";
    
    // If it's already a full URL, return as is
    if (imagePath.startsWith('https')) {
      return imagePath;
    }
    
    // Use the same domain as the API but without /api
    const baseUrl = 'https://backend.adhdrapidcare.com';
    
    // If it's a relative path, add the base URL
    if (imagePath.startsWith('/')) {
      return `${baseUrl}${imagePath}`;
    }
    
    // If it doesn't start with /, add it
    return `${baseUrl}/${imagePath}`;
  };

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get user ID from localStorage
      const storedUser = localStorage.getItem('user');
      if (!storedUser) {
        throw new Error('No user found in localStorage');
      }
      
      const parsedUser = JSON.parse(storedUser);
      const userId = parsedUser.id || parsedUser._id;
      
      if (!userId) {
        throw new Error('No user ID found');
      }
      
      // Fetch user data from API
      const response = await userAPI.getUserById(userId);
      
      if (response && response.user) {
        const userData = response.user;
        setUser({
          name: userData.name || 'Nurse',
          profileImage: userData.profileImage ? getProfileImageUrl(userData.profileImage) : 'https://cdn-icons-png.flaticon.com/512/149/149071.png',
        });
        
        // Update localStorage with fresh data
        const updatedUser = { ...parsedUser, ...userData };
        localStorage.setItem('user', JSON.stringify(updatedUser));
      } else {
        throw new Error('Invalid response from API');
      }
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError(err.message);
      
      // Fallback to localStorage data
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        setUser({
          name: parsed.name || 'Nurse',
          profileImage: parsed.profileImage ? getProfileImageUrl(parsed.profileImage) : 'https://cdn-icons-png.flaticon.com/512/149/149071.png',
        });
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserData();
  }, []);

  // Listen for custom events (when Settings component updates user data)
  useEffect(() => {
    const handleUserUpdate = () => {
      fetchUserData();
    };

    window.addEventListener('userUpdated', handleUserUpdate);

    return () => {
      window.removeEventListener('userUpdated', handleUserUpdate);
    };
  }, []);

  return (
    <header className="bg-gray-200 py-3 flex items-center justify-between border-b border-teal-200 sticky top-0 z-30">
      {/* Left: Hamburger on small screens */}
      <div className="lg:hidden">
        <button onClick={toggleSidebar} className="text-gray-700 focus:outline-none px-4">
          <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Search (hidden on small screens) */}
      <div className="hidden lg:flex items-center">
        <div className="relative">
          {/* <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search patients, appointments..."
            className="pl-10 pr-4 py-2.5 w-80 text-sm border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-teal-500"
          /> */}
        </div>
      </div>

      {/* Profile */}
      <div className="hidden lg:flex items-center space-x-4">
        {/* <div className="p-2 bg-white rounded-full shadow hover:shadow-md cursor-pointer">
          <BellIcon className="h-5 w-5 text-gray-600" />
        </div> */}
        <div className="flex items-center space-x-3 pl-4 pr-6">
          {loading ? (
            <>
              <div className="h-9 w-9 rounded-full bg-gray-200 animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
            </>
          ) : (
            <>
              <img
                src={user.profileImage}
                alt={user.name}
                className="h-9 w-9 rounded-full object-cover border-2 border-white shadow-sm"
                onError={(e) => {
                  e.target.src = "https://cdn-icons-png.flaticon.com/512/149/149071.png";
                }}
              />
              <span className="text-sm text-gray-800 font-medium">{user.name}</span>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
